import { CURRENT_SCHEMA_VERSION, type AnyVersionTemplate, type ReportTemplate } from "../types/template.js";

export interface Migration {
  from: number;
  to: number;
  description: string;
  migrate(template: AnyVersionTemplate): AnyVersionTemplate;
}

/**
 * Ordered list of migrations. Each is a pure function from version N to N+1.
 * Never edit a shipped migration; add a new one.
 */
export const MIGRATIONS: Migration[] = [
  {
    from: 0,
    to: 1,
    description: "Pre-release drafts: normalise `content` → `body`, add page regions and styles",
    migrate(t) {
      const out: AnyVersionTemplate = { ...t, schemaVersion: 1 };
      if (Array.isArray(t.content) && !t.body) {
        out.body = t.content;
        delete out.content;
      }
      out.header ??= { default: [] };
      out.footer ??= { default: [] };
      return out;
    }
  }
];

export interface MigrationResult {
  template: ReportTemplate;
  /** Migrations that were applied, in order. */
  applied: Migration[];
  fromVersion: number;
}

export class MigrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MigrationError";
  }
}

/**
 * Brings a template of any supported version up to the current schema.
 * Throws `MigrationError` for future versions or missing migration steps.
 */
export function migrateTemplate(input: AnyVersionTemplate | ReportTemplate): MigrationResult {
  const fromVersion = typeof input.schemaVersion === "number" ? input.schemaVersion : 0;
  if (fromVersion > CURRENT_SCHEMA_VERSION) {
    throw new MigrationError(`Template schemaVersion ${fromVersion} is newer than this version of BroadPaper supports (${CURRENT_SCHEMA_VERSION}). Upgrade the SDK.`);
  }
  let current: AnyVersionTemplate = structuredClone(input) as AnyVersionTemplate;
  const applied: Migration[] = [];
  let v = fromVersion;
  while (v < CURRENT_SCHEMA_VERSION) {
    const step = MIGRATIONS.find((m) => m.from === v);
    if (!step) throw new MigrationError(`No migration from schemaVersion ${v} to ${v + 1}`);
    current = step.migrate(current);
    current.schemaVersion = step.to;
    applied.push(step);
    v = step.to;
  }
  return { template: current as unknown as ReportTemplate, applied, fromVersion };
}

export function needsMigration(input: { schemaVersion?: number }): boolean {
  return (input.schemaVersion ?? 0) < CURRENT_SCHEMA_VERSION;
}
